/**
 * Course Search and Filtering Service
 * Advanced search with filters, sorting, and pagination
 */

export const courseFilters = {
    DIFFICULTY: ['Beginner', 'Intermediate', 'Advanced'],
    PRICE_RANGES: [
        { label: 'Free', min: 0, max: 0 },
        { label: 'Under $50', min: 0, max: 50 },
        { label: '$50 - $100', min: 50, max: 100 },
        { label: 'Over $100', min: 100, max: Infinity }
    ],
    RATINGS: [5, 4, 3, 2, 1],
    SORT_OPTIONS: [
        { value: 'newest', label: '📅 Newest' },
        { value: 'popular', label: '⭐ Most Popular' },
        { value: 'rating', label: '⭐ Highest Rated' },
        { value: 'price_low', label: '💰 Price: Low to High' },
        { value: 'price_high', label: '💰 Price: High to Low' },
        { value: 'trending', label: '🔥 Trending' }
    ]
};

/**
 * Filter courses based on search criteria
 */
export const filterCourses = (courses, filters) => {
    let result = [...courses];

    // Text search in title and description
    if (filters.search && filters.search.trim()) {
        const searchLower = filters.search.toLowerCase();
        result = result.filter(course =>
            course.title?.toLowerCase().includes(searchLower) ||
            course.description?.toLowerCase().includes(searchLower) ||
            course.instructor?.name?.toLowerCase().includes(searchLower)
        );
    }

    // Difficulty filter
    if (filters.difficulty && filters.difficulty.length > 0) {
        result = result.filter(course => filters.difficulty.includes(course.difficulty));
    }

    // Price range filter
    if (filters.priceRange) {
        result = result.filter(course =>
            course.price >= filters.priceRange.min &&
            course.price <= filters.priceRange.max
        );
    }

    // Rating filter
    if (filters.minRating) {
        result = result.filter(course => (course.rating || 0) >= filters.minRating);
    }

    // Category filter
    if (filters.category && filters.category.length > 0) {
        result = result.filter(course => filters.category.includes(course.category));
    }

    // Language filter
    if (filters.language && filters.language.length > 0) {
        result = result.filter(course => filters.language.includes(course.language));
    }

    // Duration filter
    if (filters.duration) {
        result = result.filter(course => {
            const duration = course.duration_hours || 0;
            switch (filters.duration) {
                case 'short':
                    return duration <= 2;
                case 'medium':
                    return duration > 2 && duration <= 10;
                case 'long':
                    return duration > 10;
                default:
                    return true;
            }
        });
    }

    return result;
};

/**
 * Sort courses based on selected option
 */
export const sortCourses = (courses, sortBy) => {
    const sorted = [...courses];

    switch (sortBy) {
        case 'newest':
            sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            break;
        case 'popular':
            sorted.sort((a, b) => (b.enrollment_count || 0) - (a.enrollment_count || 0));
            break;
        case 'rating':
            sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
            break;
        case 'price_low':
            sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
            break;
        case 'price_high':
            sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
            break;
        case 'trending':
            // Sort by recent enrollments
            sorted.sort((a, b) => {
                const aRecent = a.recent_enrollments || 0;
                const bRecent = b.recent_enrollments || 0;
                return bRecent - aRecent;
            });
            break;
        default:
            return sorted;
    }

    return sorted;
};

/**
 * Paginate courses array
 */
export const paginateCourses = (courses, page = 1, pageSize = 12) => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    return {
        data: courses.slice(startIndex, endIndex),
        pagination: {
            currentPage: page,
            pageSize,
            totalItems: courses.length,
            totalPages: Math.ceil(courses.length / pageSize),
            hasNextPage: endIndex < courses.length,
            hasPreviousPage: page > 1
        }
    };
};

/**
 * Complete search and filter pipeline
 */
export const searchAndFilterCourses = (courses, searchParams) => {
    const {
        filters = {},
        sortBy = 'newest',
        page = 1,
        pageSize = 12
    } = searchParams;

    // Apply filters
    let result = filterCourses(courses, filters);

    // Apply sorting
    result = sortCourses(result, sortBy);

    // Apply pagination
    const paginated = paginateCourses(result, page, pageSize);

    return paginated;
};

export default {
    courseFilters,
    filterCourses,
    sortCourses,
    paginateCourses,
    searchAndFilterCourses
};
